import React, { useEffect, useState } from "react";

import { useTranslation } from "utils";

import { AntModal, Button, Icon, Typography } from "components";

interface TransitionLoadingModalProps {
  isVisible: boolean;
  onExplore: () => void;
  /** Headline switches to "no rotation detected" when false. */
  cropRotationLoaded?: boolean;
  /** Whether to animate the calculation step list. Defaults to `cropRotationLoaded`. */
  calculationStepsVisible?: boolean;
}

const STEP_DELAYS_MS = [0, 500, 1000];
const CTA_DELAY_MS = 1600;

export const TransitionLoadingModal = ({ isVisible, onExplore, cropRotationLoaded = true, calculationStepsVisible }: TransitionLoadingModalProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const showCalculationSteps = calculationStepsVisible ?? cropRotationLoaded;
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [showCta, setShowCta] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    setVisibleSteps(0);
    setShowCta(!showCalculationSteps);

    if (!showCalculationSteps) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    STEP_DELAYS_MS.forEach((delay, index) => {
      timers.push(setTimeout(() => setVisibleSteps(index + 1), delay));
    });
    timers.push(setTimeout(() => setShowCta(true), CTA_DELAY_MS));

    return () => timers.forEach(clearTimeout);
  }, [isVisible, showCalculationSteps]);

  const steps = [t("transitionModal.step1"), t("transitionModal.step2"), t("transitionModal.step3")];

  return (
    <AntModal
      open={isVisible}
      footer={null}
      closable={false}
      centered
      width={450}
      classNames={{ mask: "transition-loading-modal-mask" }}
      className="transition-loading-modal-ant"
      data-testid="transition-loading-modal"
    >
      <div className="transition-loading-modal-content" data-testid={`transition-loading-modal-content-${cropRotationLoaded ? "loaded" : "no-rotation"}`}>
        <Typography variant="h3" className="transition-loading-modal-headline">
          {cropRotationLoaded ? t("transitionModal.headline") : t("transitionModal.noCropRotationHeadline")}
        </Typography>
        {showCalculationSteps && (
          <ul className="transition-loading-modal-steps">
            {steps.map((step, index) => (
              <li key={step} className={`transition-loading-modal-step${visibleSteps > index ? " transition-loading-modal-step--visible" : ""}`}>
                <Icon iconName="tabler/circle-check-filled" size="m" className="transition-loading-modal-step-icon" />
                <Typography variant="paragraph-m">{step}</Typography>
              </li>
            ))}
          </ul>
        )}
        <div className={`transition-loading-modal-cta${showCta ? " transition-loading-modal-cta--visible" : ""}`}>
          <Button type="primary" block onClick={onExplore} dataTestId="transition-loading-modal-explore-btn">
            {t("transitionModal.cta")}
          </Button>
        </div>
      </div>
    </AntModal>
  );
};

export default TransitionLoadingModal;
